import { apiFetch } from "./api";
import type {
  BackendUserRole,
  BackendUserStatus,
  BulkImportResult,
  BulkImportRow,
  Paginated,
} from "./platform-api";

export interface TenantUser {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  /** Legacy local handle, no longer set. Sign-in is by email only (ADR-0017). */
  username: string | null;
  phone: string | null;
  /** Managed department (ADR-0008): id + resolved display name. */
  departmentId: string | null;
  department: string | null;
  /** Full org path "Company › Division › Unit" (ADR-0010). */
  departmentPath: string | null;
  /** Visibility scope (FEAT-2026-005): null = tenant-wide. */
  scopeDepartmentId: string | null;
  scopeDepartmentPath: string | null;
  roleType: BackendUserRole;
  roleLabel: string | null;
  status: BackendUserStatus;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface ListUsersQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: BackendUserStatus;
  roleType?: BackendUserRole;
}

export interface CreateUserBody {
  name: string;
  email: string;
  phone?: string;
  /** Managed department id (ADR-0008). */
  departmentId?: string;
  /** Visibility scope (FEAT-2026-005) — TENANT_OWNER only. */
  scopeDepartmentId?: string;
  roleType: BackendUserRole;
  roleLabel?: string;
  status?: BackendUserStatus;
}

export interface UpdateUserBody {
  name?: string;
  phone?: string;
  departmentId?: string;
  /** Omit = no change; null = clear (tenant-wide). TENANT_OWNER only. */
  scopeDepartmentId?: string | null;
  roleType?: BackendUserRole;
  roleLabel?: string;
  status?: BackendUserStatus;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const tenantUsersApi = {
  list: (q: ListUsersQuery = {}) =>
    apiFetch<Paginated<TenantUser>>(`/users${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<TenantUser>(`/users/${id}`),

  create: (body: CreateUserBody) =>
    // Three result shapes:
    //   - new invitation: `inviteUrl` set, `linkedToExisting=false`.
    //   - re-using an existing Clerk identity (e.g. the person is also
    //     an Employee on the mobile app): `inviteUrl=null`,
    //     `linkedToExisting=true`. They keep their existing password.
    //   - the rare no-Clerk error path → throws.
    apiFetch<TenantUser & { inviteUrl: string | null; linkedToExisting: boolean }>(
      `/users`,
      { method: "POST", body },
    ),

  update: (id: string, body: UpdateUserBody) =>
    apiFetch<TenantUser>(`/users/${id}`, { method: "PATCH", body }),

  disable: (id: string) =>
    apiFetch<{ id: string; status: BackendUserStatus }>(`/users/${id}`, { method: "DELETE" }),

  enable: (id: string) =>
    apiFetch<TenantUser>(`/users/${id}/enable`, { method: "PATCH" }),

  bulkImport: (rows: BulkImportRow[]) =>
    apiFetch<BulkImportResult>(`/users/import`, { method: "POST", body: { rows } }),
};
